import React, { useCallback, useEffect, useMemo } from 'react';
import clsx from 'clsx';
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControlLabel,
  Grid,
  Switch,
  Typography,
  makeStyles
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import ClearIcon from '@material-ui/icons/Clear';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';

import { DatePickerRangeComponent, SelectionChip } from 'src/components';
import { slices, useAppDispatch, useAppSelector } from 'src/redux';
import { DateFieldsV2, DateRangeSpecific } from 'src/types';
import { usePermissions, useSnackBar } from 'src/hooks';
import { map } from 'lodash';
import { dateToday, toUtcEquivalentDate } from 'src/utils';
import useUserInfo from 'src/hooks/user/use-user-info';
import { multiBranchFeat } from 'src/constants/feature-toggle';
import { BranchListDropDown } from 'src/components/dropdown';
import useResolution from 'src/hooks/useResolution';
import { PaymentTypeDropDown } from 'src/components/dropdown/PaymentTypeDropDown';

interface Props {
  onApplyFilterPress: (filter: any) => void;
  className?: string;
  isBasicDashboard?: boolean;
}

const { actions: salesActions, selectors: salesSelectors } = slices.sales;

const useStyles = makeStyles((theme) => ({
  root: {},
  importButton: {
    marginRight: theme.spacing(1)
  },
  exportButton: {
    marginRight: theme.spacing(1)
  },
  cardContent: {
    flexDirection: 'row'
  },
  datePickersContainer: {
    marginTop: theme.spacing(1),
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center'
  },
  dashSeparator: {
    marginRight: theme.spacing(1),
    marginLeft: theme.spacing(1)
  },
  chip: {
    marginRight: theme.spacing(0.5),
    marginLeft: theme.spacing(0.5)
  }
}));

const Toolbar = ({
  className,
  onApplyFilterPress,
  isBasicDashboard = false,
  ...rest
}: Props) => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const snackBar = useSnackBar();
  const salesFilter = useAppSelector(salesSelectors.selectAllSalesFilter);
  const ecommFilterVal = useAppSelector(salesSelectors.selectEcommFilter);
  const permissions = usePermissions();
  const { getUserDetails } = useUserInfo();
  const { isMobile } = useResolution();

  const areDateRangeFieldEmpty = useMemo(() => {
    // Check for Date Range Fields if its empty.
    const arr = [
      salesFilter?.encoded_date_from,
      salesFilter?.encoded_date_to,
      salesFilter?.purchased_date_from,
      salesFilter?.purchased_date_to,
      salesFilter?.sold_date_from,
      salesFilter?.sold_date_from
    ];
    const mapped = map(arr, (x) => !x);
    return mapped.every((v) => v === true);
  }, [salesFilter]);

  const handleBranchChange = useCallback(
    (v: number[]) => {
      dispatch(salesActions.updateFilter({ branch_ids: v }));
    },
    [dispatch]
  );

  const toggleEcommFilter = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const switchedBoolean = event.target.checked;
      dispatch(salesActions.toggleEcommOnlyFilter(switchedBoolean ? 1 : 0));
    },
    [dispatch]
  );

  const toggleVoucherFilter = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const switchedBoolean = event.target.checked;
      dispatch(salesActions.toggleVoucherOnlyFilter(switchedBoolean ? 1 : 0));
    },
    [dispatch]
  );

  const handleSelectPaymentTypes = useCallback(
    (paymentType: string[] | string) => {
      const values = Array.isArray(paymentType)
        ? paymentType
        : paymentType
        ? [paymentType]
        : [];
      dispatch(salesActions.updateFilter({ payment_type: values }));
    },
    [dispatch]
  );

  const validateDatePair = (
    from: string | null,
    to: string | null,
    label: string
  ) => {
    if (from && !to) {
      snackBar.show({
        severity: 'error',
        message: `Please provide ${label} date to`
      });
      return false;
    }
    if (to && !from) {
      snackBar.show({
        severity: 'error',
        message: `Please provide ${label} date from`
      });
      return false;
    }
    return true;
  };

  const onInternalApply = useCallback(() => {
    let filterToUse = salesFilter;

    if (isBasicDashboard) {
      const basicPreset =
        salesFilter?.preset_dates === 'yesterday' ? 'yesterday' : 'today';
      filterToUse = {
        ...salesFilter,
        sold_date_from: null,
        sold_date_to: null,
        encoded_date_from: null,
        encoded_date_to: null,
        purchased_date_from: null,
        purchased_date_to: null,
        preset_dates: basicPreset,
        branch_ids: salesFilter?.branch_ids || [],
        payment_type: salesFilter?.payment_type || [],
        ecomm_transactions_only: 0,
        with_voucher_only: 0
      };
      dispatch(salesActions.updateFilter(filterToUse));
      onApplyFilterPress(filterToUse);
      return;
    }

    // If no quick filter is not applied and no Date Sold (Customer), Date Purchased (Supplier), Date Encoded is inputted default to today.
    if (salesFilter.sold_date_to && !salesFilter.sold_date_from) {
      snackBar.show({
        severity: 'error',
        message: 'Please provide sold date from'
      });
      return;
    }

    if (salesFilter.purchased_date_to && !salesFilter.purchased_date_from) {
      snackBar.show({
        severity: 'error',
        message: 'Please provide purchased date from'
      });
      return;
    }

    if (salesFilter.encoded_date_to && !salesFilter.encoded_date_from) {
      snackBar.show({
        severity: 'error',
        message: 'Please provide encoded date from'
      });
      return;
    }

    if (
      !validateDatePair(
        salesFilter.sold_date_from ?? null,
        salesFilter.sold_date_to ?? null,
        'sold'
      )
    ) {
      snackBar.show({
        severity: 'error',
        message: 'Please provide proper sold date range fields'
      });
      return;
    }

    if (
      !validateDatePair(
        salesFilter.purchased_date_from ?? null,
        salesFilter.purchased_date_to ?? null,
        'purchased'
      )
    ) {
      snackBar.show({
        severity: 'error',
        message: 'Please provide proper purchased date range fields'
      });
      return;
    }

    if (
      !validateDatePair(
        salesFilter.encoded_date_from ?? null,
        salesFilter.encoded_date_to ?? null,
        'encoded'
      )
    ) {
      snackBar.show({
        severity: 'error',
        message: 'Please provide proper encoded date range fields'
      });
      return;
    }

    if (
      (areDateRangeFieldEmpty && !salesFilter?.preset_dates) ||
      !salesFilter
    ) {
      filterToUse = { ...salesFilter, preset_dates: 'today' };
      dispatch(salesActions.setFilterToday());
    }
    // Date Range are prioritized. If DateRange is Selected it would remove QuickFilter
    if (!areDateRangeFieldEmpty && salesFilter?.preset_dates) {
      dispatch(salesActions.removeQuickFilter());
    }

    onApplyFilterPress(filterToUse);
  }, [
    areDateRangeFieldEmpty,
    dispatch,
    isBasicDashboard,
    onApplyFilterPress,
    salesFilter,
    snackBar
  ]);

  const onChangedDate = useCallback(
    (date: MaterialUiPickersDate, field: DateFieldsV2) => {
      let dateOnStr = toUtcEquivalentDate(date)?.toISOString() || null;

      dispatch(salesActions.updateFilterDate({ date: dateOnStr, field }));
    },
    [dispatch]
  );

  const onPressClear = useCallback(() => {
    dispatch(salesActions.resetFilter());
  }, [dispatch]);

  const onQuickFilterChange = useCallback(
    (field: DateRangeSpecific) => {
      if (field === salesFilter?.preset_dates) {
        dispatch(salesActions.updateQuickFilter(undefined));
        return;
      }
      dispatch(salesActions.updateQuickFilter(field));
    },
    [dispatch, salesFilter]
  );

  const isChipSelected = useCallback(
    (rangeField: DateRangeSpecific) => rangeField === salesFilter?.preset_dates,
    [salesFilter]
  );

  const RenderQuickFilters = useCallback(() => {
    return (
      <Grid
        item
        xl={12}
        container
        direction="column"
        alignItems="center"
        style={{ rowGap: '12px' }}
      >
        <Grid container spacing={2}>
          <Grid
            item
            md={3}
            sm={3}
            xs={6}
            style={{ display: 'flex', justifyContent: 'center' }}
          >
            <SelectionChip
              label="Yesterday"
              className={classes.chip}
              isSelected={isChipSelected('yesterday')}
              onClick={() => onQuickFilterChange('yesterday')}
              width={'92px'}
            />
          </Grid>
          <Grid
            item
            md={3}
            sm={3}
            xs={6}
            style={{ display: 'flex', justifyContent: 'center' }}
          >
            <SelectionChip
              label="Today"
              className={classes.chip}
              isSelected={isChipSelected('today')}
              onClick={() => onQuickFilterChange('today')}
              width={'92px'}
            />
          </Grid>
          <Grid
            item
            md={3}
            sm={3}
            xs={6}
            style={{ display: 'flex', justifyContent: 'center' }}
          >
            {!isBasicDashboard ? (
              <SelectionChip
                label="This Week"
                className={classes.chip}
                isSelected={isChipSelected('week')}
                onClick={() => onQuickFilterChange('week')}
                width={'92px'}
              />
            ) : null}
          </Grid>
          <Grid
            item
            sm={3}
            md={3}
            xs={6}
            style={{ display: 'flex', justifyContent: 'center' }}
          >
            {!isBasicDashboard ? (
              <SelectionChip
                label="This Month"
                className={classes.chip}
                isSelected={isChipSelected('month')}
                onClick={() => onQuickFilterChange('month')}
                width={'92px'}
              />
            ) : null}
          </Grid>
        </Grid>

        {!isBasicDashboard ? (
          <Grid container spacing={2} justifyContent="center">
            <Grid
              item
              xs={12}
              sm={6}
              md={6}
              style={{ display: 'flex', justifyContent: 'center' }}
            >
              <Box style={{ width: '255px' }}>
                <FormControlLabel
                  label="Ecomm Transactions Only"
                  control={
                    <Switch
                      color="primary"
                      checked={ecommFilterVal === 1}
                      onChange={toggleEcommFilter}
                    />
                  }
                />
              </Box>
            </Grid>
            <Grid
              item
              xs={12}
              sm={6}
              md={6}
              style={{ display: 'flex', justifyContent: 'center' }}
            >
              <Box style={{ width: '255px' }}>
                <FormControlLabel
                  label="With Vouchers Only"
                  control={
                    <Switch
                      color="primary"
                      checked={salesFilter?.with_voucher_only === 1}
                      onChange={toggleVoucherFilter}
                    />
                  }
                />
              </Box>
            </Grid>
          </Grid>
        ) : null}
      </Grid>
    );
  }, [
    classes,
    ecommFilterVal,
    isBasicDashboard,
    isChipSelected,
    onQuickFilterChange,
    toggleEcommFilter,
    toggleVoucherFilter,
    salesFilter?.with_voucher_only
  ]);

  const RenderButtonsGroup = useCallback(() => {
    return (
      <Grid container spacing={2} justifyContent="center">
        {!isBasicDashboard ? (
          <Grid item xs={6} sm="auto">
            <Button
              endIcon={<ClearIcon />}
              onClick={onPressClear}
              color="secondary"
              fullWidth
            >
              Set To Default
            </Button>
          </Grid>
        ) : null}
        <Grid item xs={isBasicDashboard ? 12 : 6} sm="auto">
          <Button
            disabled={
              !permissions?.canViewSales &&
              !permissions?.canViewSalesBasicDashboard
            }
            endIcon={<SearchIcon />}
            onClick={onInternalApply}
            color="primary"
            variant="contained"
            fullWidth
          >
            Apply Filter
          </Button>
        </Grid>
      </Grid>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isBasicDashboard,
    onInternalApply,
    onPressClear,
    permissions.canViewSales,
    permissions.canViewSalesBasicDashboard
  ]);

  const RenderFilters = useCallback(() => {
    return (
      <Grid container spacing={4}>
        <Grid item xs={12} md={6}>
          <Grid container direction="column" style={{ rowGap: '2em' }}>
            <Grid item>
              <DatePickerRangeComponent
                title="Date Sold to Customer"
                fromDateMax={dateToday()}
                fromDateValue={salesFilter?.sold_date_from}
                toDateValue={salesFilter?.sold_date_to}
                toDateMin={salesFilter?.sold_date_from}
                dateToLabel="To"
                dateFromLabel="From"
                onChangeToDate={(date) => onChangedDate(date, 'sold_date_to')}
                onChangeFromDate={(date) =>
                  onChangedDate(date, 'sold_date_from')
                }
                fullwidth
              />
            </Grid>
            <Grid item>
              <DatePickerRangeComponent
                title="Date Purchased from Supplier"
                fromDateMax={dateToday()}
                fromDateValue={salesFilter?.purchased_date_from}
                toDateValue={salesFilter?.purchased_date_to}
                toDateMin={salesFilter?.purchased_date_from}
                dateToLabel="To"
                dateFromLabel="From"
                onChangeToDate={(date) =>
                  onChangedDate(date, 'purchased_date_to')
                }
                onChangeFromDate={(date) =>
                  onChangedDate(date, 'purchased_date_from')
                }
                fullwidth
              />
            </Grid>
            <Grid item>
              <DatePickerRangeComponent
                title="Date Encoded"
                toDateValue={salesFilter?.encoded_date_to}
                toDateMin={salesFilter?.encoded_date_from}
                fromDateMax={dateToday()}
                fromDateValue={salesFilter?.encoded_date_from}
                dateToLabel="To"
                dateFromLabel="From"
                onChangeToDate={(date) =>
                  onChangedDate(date, 'encoded_date_to')
                }
                onChangeFromDate={(date) =>
                  onChangedDate(date, 'encoded_date_from')
                }
                fullwidth
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12} md={6}>
          {multiBranchFeat ? (
            <Grid item style={{ marginTop: isMobile ? undefined : '29.34px' }}>
              <BranchListDropDown
                multiple
                defaultValue={salesFilter?.branch_ids || []}
                onHandleBranchChange={(branchIds?: number[]) => {
                  handleBranchChange(branchIds || []);
                }}
              />
            </Grid>
          ) : null}

          <Grid item style={{ marginTop: isMobile ? '2em' : '61.02px' }}>
            <PaymentTypeDropDown
              isMultiple={true}
              initialValue={salesFilter?.payment_type || []}
              onHandleChange={handleSelectPaymentTypes}
              limitTags={3}
            />
          </Grid>
        </Grid>
      </Grid>
    );
  }, [salesFilter, onChangedDate, handleBranchChange]);

  useEffect(() => {
    getUserDetails();
  }, [getUserDetails]);

  const RenderBranchAndPayment = useCallback(() => {
    return (
      <Grid container spacing={2}>
        {multiBranchFeat ? (
          <Grid item xs={12} sm={6}>
            <BranchListDropDown
              multiple
              defaultValue={salesFilter?.branch_ids || []}
              onHandleBranchChange={(branchIds?: number[]) => {
                handleBranchChange(branchIds || []);
              }}
            />
          </Grid>
        ) : null}
        <Grid item xs={12} sm={6}>
          <PaymentTypeDropDown
            isMultiple={true}
            initialValue={salesFilter?.payment_type || []}
            onHandleChange={handleSelectPaymentTypes}
            limitTags={3}
          />
        </Grid>
      </Grid>
    );
  }, [salesFilter, handleBranchChange, handleSelectPaymentTypes]);

  return (
    <div className={clsx(classes.root, className)} {...rest}>
      <Box display="flex" flexDirection="row" justifyContent="space-between">
        <Typography color="textPrimary" gutterBottom variant="h3">
          Sales
        </Typography>
      </Box>
      <Box mt={3}>
        <Card>
          <CardContent>
            {!isBasicDashboard ? RenderFilters() : RenderBranchAndPayment()}

            <Grid
              container
              direction="column"
              alignItems="center"
              style={{
                marginTop: '1em',
                rowGap: '1em'
              }}
            >
              <Grid item xs={12}>
                <Grid
                  container
                  alignItems="center"
                  justifyContent={isMobile ? undefined : 'center'}
                  spacing={2}
                >
                  <Grid item>
                    <Typography
                      variant="h5"
                      style={{
                        fontWeight: 'normal',
                        textAlign: isMobile ? undefined : 'left'
                      }}
                    >
                      Preset Filters
                    </Typography>
                  </Grid>
                  {RenderQuickFilters()}
                </Grid>
              </Grid>

              {/* Buttons group below */}
              <Grid item xs={12}>
                {RenderButtonsGroup()}
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>
    </div>
  );
};

export default Toolbar;
